import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Input, Button, Card, Typography, message, Space, Select, DatePicker, Descriptions, Spin } from 'antd';
import styled from 'styled-components';
import dayjs from 'dayjs';
import PersistentDrawerLeft from "../../reusable/admin/Main";
import { restApi } from "../../../constants/api";

const { Title } = Typography;
const { Option } = Select;

const StyledContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 20px;
  background-color: #f9f9f9;
`;

const StyledCard = styled(Card)`
  max-width: 700px;
  width: 100%;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  border-radius: 16px;
`;

const StyledButton = styled(Button)`
  background-color: #1677ff;
  border-radius: 8px;
  width: 100%;
  height: 40px;
  font-weight: 500;
  font-size: 16px;

  &:hover {
    background-color: #0056d2;
  }
`;

interface UserType {
  id: string;
  name: string;
  email: string;
  mobileNo: string;
  user_type: string;
  total_number_of_days: number;
}

const UpdateAttendanceCycle = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const [users, setUsers] = useState<UserType[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [endDate, setEndDate] = useState<string | null>(null);

  // Fetch users
  useEffect(() => {
    restApi('/api/users/dropdown', 'GET').then((res) => {
      const response = res as { data: UserType[] };
      setUsers(response.data || []);
    });
  }, []);

  // Fetch attendance cycle details
  useEffect(() => {
    const fetchCycle = async () => {
      if (id) {
        try {
          const res: any = await restApi(`/api/users/cycles/${id}`, 'GET');
          const data = res?.data;
          if (data) {
            const cycleData = data[0];

            form.setFieldsValue({
              userId: cycleData.user_id,
              start_date: dayjs(cycleData.start_date),
            });

            const selected = users.find((u) => u.id === cycleData.user_id);
            if (selected) {
              setSelectedUser(selected);
              if (selected.total_number_of_days) {
                const end = dayjs(cycleData.start_date).add(selected.total_number_of_days, 'day');
                setEndDate(end.format('YYYY-MM-DD'));
              }
            }
          }
          setPageLoading(false);
        } catch (err) {
          message.error('Failed to fetch cycle details');
          setPageLoading(false);
        }
      }
    };

    if (users.length > 0) {
      fetchCycle();
    }
  }, [id, users]);

  // When user selects different user
  const handleUserChange = (value: string) => {
    const user = users.find((u) => u.id === value);
    setSelectedUser(user || null);
    form.setFieldValue("userId", value);

    if (form.getFieldValue("start_date") && user) {
      calculateEndDate(form.getFieldValue("start_date"), user.total_number_of_days);
    }
  };

  // Recalculate end date
  const calculateEndDate = (startDate: any, days: number) => {
    if (days > 0 && startDate) {
      const end = dayjs(startDate).add(days, 'day');
      setEndDate(end.format('YYYY-MM-DD'));
    }
  };

  // When start date changes
  const handleStartDateChange = (date: any) => {
    if (date && selectedUser?.total_number_of_days) {
      calculateEndDate(date, selectedUser.total_number_of_days);
    }
  };

  // Submit updated form
  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      // setLoading(true);
      const payload = {
        userId: values.userId,
        start_date: values.start_date.format("YYYY-MM-DD"),
        end_date: endDate,
      };

      await restApi(`/api/users/cycles/${id}`, 'PATCH', payload);

      message.success("Attendance cycle updated successfully!");
      form.resetFields();
      setSelectedUser(null);
      setEndDate(null);
      // setLoading(false);
      navigate('/admin/users/cycles');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong";
      message.error(errorMessage);
      // setLoading(false);
    } finally {
      setLoading(false); // ✅ Always run to stop the loader
    }
  };

  return (
    <PersistentDrawerLeft>
      <StyledContainer>
        <StyledCard>
          {pageLoading ? (
            <Spin size="large" style={{ width: '100%', marginTop: 50 }} />
          ) : (
            <>
              <Title level={3} style={{ textAlign: 'center', marginBottom: 30 }}>
                Update Attendance Cycle
              </Title>

              <Form
                layout="vertical"
                form={form}
                onFinish={onFinish}
                autoComplete="off"
              >
                <Form.Item
                  label="Select User"
                  name="userId"
                  rules={[{ required: true, message: 'Please select a user' }]}

                >
                  <Select placeholder="Select a user" onChange={handleUserChange} disabled>
                    {users.map((u) => (
                      <Option key={u.id} value={u.id}>
                        {u.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                {selectedUser && (
                  <Descriptions
                    column={1}
                    bordered
                    size="small"
                    style={{ marginBottom: 20 }}
                    labelStyle={{ fontWeight: 'bold' }}
                  >
                    <Descriptions.Item label="Name">{selectedUser.name}</Descriptions.Item>
                    <Descriptions.Item label="Email">{selectedUser.email}</Descriptions.Item>
                    <Descriptions.Item label="Mobile No">{selectedUser.mobileNo}</Descriptions.Item>
                    <Descriptions.Item label="User Type">{selectedUser.user_type}</Descriptions.Item>
                    <Descriptions.Item label="Total Number of Days">{selectedUser.total_number_of_days}</Descriptions.Item>
                  </Descriptions>
                )}

                <Form.Item
                  label="Start Date"
                  name="start_date"
                  rules={[{ required: true, message: 'Please select a start date' }]}
                >
                  <DatePicker style={{ width: '100%' }} onChange={handleStartDateChange} />
                </Form.Item>

                {endDate && (
                  <Form.Item label="Calculated End Date">
                    <Input value={endDate} readOnly />
                  </Form.Item>
                )}

                <Form.Item>
                  <Space style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
                    <StyledButton type="primary" htmlType="submit" loading={loading} disabled={loading}>
                      {/* Update */}
                      {loading ? 'Updating...' : 'Update'}
                    </StyledButton>
                    <Button onClick={() => navigate(-1)}>
                      Cancel
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </>
          )}
        </StyledCard>
      </StyledContainer>
    </PersistentDrawerLeft>
  );
};

export default UpdateAttendanceCycle;
